import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { ByIV } from '../../score-contributions/types';
interface UnitWithPrice {
  shortLabel?: string;
  label: string;
  unitPrice: number;
}

// https://www.ccfpd.org/Portals/0/Assets/PDF/Facts_Chart.pdf
const carbonPrice = 417;

export type PhysicalComparisonLabel = (typeof _unitPrices)[ImpactCategoryId][
  | Valence.NEGATIVE
  | Valence.POSITIVE][number]['label'];

// https://docs.google.com/spreadsheets/d/18hjFcxzxQjae8H9WZGqzRL5rO3AgiFaqZXWY1lWyxnQ/edit#gid=2001947243
const _unitPrices = {
  [ImpactCategoryId.JOBS]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        label: 'direct or indirect jobs',
        unitPrice: 7_964,
      },
    ],
  },
  [ImpactCategoryId.TAXES]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        label: 'days of primary education',
        unitPrice: 58.08,
      },
      {
        shortLabel: 'covered under health care',
        label: 'person-years of primary health care',
        unitPrice: 523,
      },
    ],
  },
  [ImpactCategoryId.SOCIETAL_INFRA]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        label: 'long-haul train rides',
        unitPrice: 35,
      },
      {
        shortLabel: 'apartment buildings',
        label: 'five-storey apartment buildings',
        unitPrice: 14_500_000,
      },
      {
        label: 'days of elderly care',
        unitPrice: 48.4,
      },
    ],
  },
  [ImpactCategoryId.SOCIETAL_STABILITY]: {
    [Valence.NEGATIVE]: [
      {
        label: 'half carat natural diamonds',
        unitPrice: 980,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'hours of interpretation services',
        unitPrice: 26.6,
      },
      {
        label: 'hours of cultural education',
        unitPrice: 4.9,
      },
    ],
  },
  [ImpactCategoryId.EQUALITY]: {
    [Valence.NEGATIVE]: [
      {
        label: 'articles of fast fashion clothing',
        unitPrice: 1,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'packages of birth control pills',
        unitPrice: 4.0,
      },
      {
        label: 'emergency shelter tents',
        unitPrice: 66,
      },
    ],
  },
  [ImpactCategoryId.KNOWLEDGE_INFRA]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        label: 'personal computers',
        unitPrice: 151.68,
      },
      {
        label: 'months of internet broadband',
        unitPrice: 9.35,
      },
    ],
  },
  [ImpactCategoryId.CREATES_KNOWLEDGE]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        shortLabel: 'clinical trials',
        label: 'phase 3 trials of cardiovascular medicine',
        unitPrice: 8_265_600.0,
      },
      {
        shortLabel: 'hours of engineering services',
        label: 'hours of mechanical prototyping services',
        unitPrice: 52.09,
      },
    ],
  },
  [ImpactCategoryId.DISTRIBUTING_KNOWLEDGE]: {
    [Valence.NEGATIVE]: [
      {
        shortLabel: 'fake news articles',
        label: 'online fake news articles that reach 5M readers',
        unitPrice: 1260,
      },
    ],
    [Valence.POSITIVE]: [
      {
        shortLabel: 'days of BBC news',
        label: 'days of BBC television news programming',
        unitPrice: 645_120,
      },
      {
        label: 'science lessons',
        unitPrice: 12.38,
      },
    ],
  },
  [ImpactCategoryId.SCARCE_HUMAN_CAPITAL]: {
    [Valence.NEGATIVE]: [
      {
        shortLabel: 'scarce worker years',
        label: 'person-years of scarce, highly educated workers',
        unitPrice: 60_814,
      },
    ],
    [Valence.POSITIVE]: [],
  },
  [ImpactCategoryId.PHYSICAL_DISEASES]: {
    [Valence.NEGATIVE]: [
      {
        shortLabel: 'life years',
        label: 'disability-adjusted life years (DALY)',
        unitPrice: 12_235,
      },
      {
        label: 'cigarettes',
        unitPrice: 5.0,
      },
    ],
    [Valence.POSITIVE]: [
      {
        shortLabel: 'life years',
        label: 'disability-adjusted life years (DALY)',
        unitPrice: 12_235,
      },
      {
        label: 'mosquito nets',
        unitPrice: 12.84,
      },
    ],
  },
  [ImpactCategoryId.MENTAL_DISEASES]: {
    [Valence.NEGATIVE]: [
      {
        shortLabel: 'life years',
        label: 'disability-adjusted life years (DALY)',
        unitPrice: 12_235,
      },
      {
        label: 'online gambling sessions',
        unitPrice: 0.53,
      },
    ],
    [Valence.POSITIVE]: [
      {
        shortLabel: 'life years',
        label: 'disability-adjusted life years (DALY)',
        unitPrice: 12_235,
      },
      {
        label: 'psychotherapy sessions',
        unitPrice: 51.2,
      },
    ],
  },
  [ImpactCategoryId.NUTRITION]: {
    [Valence.NEGATIVE]: [],
    [Valence.POSITIVE]: [
      {
        label: 'bananas',
        unitPrice: 0.22,
      },
      {
        label: 'ready meals',
        unitPrice: 3.39,
      },
    ],
  },
  [ImpactCategoryId.RELATIONSHIPS]: {
    [Valence.NEGATIVE]: [
      {
        label: 'bottles of vodka',
        unitPrice: 4.0,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'phone calls',
        unitPrice: 0.1776,
      },
      {
        label: 'hours of couples therapy',
        unitPrice: 216,
      },
    ],
  },
  [ImpactCategoryId.MEANING_AND_JOY]: {
    [Valence.NEGATIVE]: [
      {
        shortLabel: 'social media use',
        label: 'hours of meaningless social media scrolling',
        unitPrice: 0.05,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'roller coaster rides',
        unitPrice: 2.16,
      },
      {
        label: 'chocolate bars',
        unitPrice: 0.18,
      },
    ],
  },
  [ImpactCategoryId.GHG_EMISSIONS]: {
    [Valence.NEGATIVE]: [
      {
        label: 'tons of GHG emissions',
        unitPrice: carbonPrice,
      },
      {
        shortLabel: 'households',
        label: 'average annual emissions of U.S. households',
        unitPrice: 43.01,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'tons of GHG emissions',
        unitPrice: carbonPrice,
      },
      {
        shortLabel: 'households',
        label: 'average annual emissions of U.S. households',
        unitPrice: 43.01,
      },
    ],
  },
  [ImpactCategoryId.NON_GHG_EMISSIONS]: {
    [Valence.NEGATIVE]: [
      {
        label: 'cubic meters of spilled oil',
        unitPrice: 28_124,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'cubic meters of remediated soil',
        unitPrice: 23.6,
      },
    ],
  },
  [ImpactCategoryId.SCARCE_NATURAL_RESOURCES]: {
    [Valence.NEGATIVE]: [
      {
        label: 'dishwasher cycles',
        unitPrice: 0.88,
      },
      {
        label: 'kilograms of germanium',
        unitPrice: 1226,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'cubic meters of treated wastewater',
        unitPrice: 0.11,
      },
    ],
  },
  [ImpactCategoryId.BIODIVERSITY]: {
    [Valence.NEGATIVE]: [
      {
        label: 'square meters of felled forest',
        unitPrice: 216,
      },
      {
        label: 'highway kilometers',
        unitPrice: 2_314_000,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'square meters of planted forest',
        unitPrice: 216,
      },
      {
        shortLabel: 'conservation hours',
        label: 'hours of biodiversity conservation services',
        unitPrice: 39.6,
      },
    ],
  },
  [ImpactCategoryId.WASTE]: {
    [Valence.NEGATIVE]: [
      {
        label: 'plastic bags',
        unitPrice: 0.22,
      },
    ],
    [Valence.POSITIVE]: [
      {
        label: 'cubic meters of treated oil waste',
        unitPrice: 3.66,
      },
    ],
  },
} as const;

// TODO ML: Replace bogey export with "satisfies" after we get TS upgrade
export const unitPrices: ByIV<readonly UnitWithPrice[]> = _unitPrices;
